/**
 * [계정 관리 페이지]
 * 목적: master가 관리자 계정 목록 조회 · 신규 생성 · 수정 · 비활성화
 */
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Chip, Button, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Select, MenuItem,
  FormControl, InputLabel, Alert, Stack,
} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import api from '../api/client';
import { actionBtnSx, statusChipSx, tableTagSx } from '../styles/uiTokens';

const roleMap = {
  master:  { label: 'MASTER',  color: '#E91E63' },
  manager: { label: 'MANAGER', color: '#1976d2' },
};

const EMPTY_FORM = { email: '', password: '', name: '', role: 'manager', storeId: '', memo: '' };

export default function AdminsPage() {
  const [admins, setAdmins] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(false);

  // 생성/수정 다이얼로그
  const [mode, setMode] = useState(null); // 'create' | 'edit'
  const [editTarget, setEditTarget] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  // 비밀번호 재설정 다이얼로그
  const [pwTarget, setPwTarget] = useState(null);
  const [pwForm, setPwForm] = useState({ password: '', confirm: '' });
  const [pwError, setPwError] = useState('');
  const [pwSaving, setPwSaving] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // 매장 관리 페이지에서 "매니저 추가"로 넘어온 경우: 생성 다이얼로그를 매니저+해당 매장 프리셋으로 연다.
    fetchData().then(() => {
      const presetStoreId = location.state?.presetStoreId;
      if (presetStoreId) {
        setMode('create');
        setForm({ ...EMPTY_FORM, role: 'manager', storeId: presetStoreId });
        setFormError('');
        // state 소비 후 제거 — 새로고침/뒤로가기 시 다이얼로그 재오픈 방지
        navigate(location.pathname, { replace: true, state: null });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [adminsRes, storesRes] = await Promise.all([
        api.get('/admins'),
        api.get('/stores'),
      ]);
      setAdmins(adminsRes.data);
      setStores(storesRes.data);
    } finally {
      setLoading(false);
    }
  };

  // ── 생성 다이얼로그 열기 ─────────────────────────────
  const handleOpenCreate = () => {
    setMode('create');
    setForm(EMPTY_FORM);
    setFormError('');
  };

  // ── 수정 다이얼로그 열기 ─────────────────────────────
  const handleOpenEdit = (admin) => {
    setMode('edit');
    setEditTarget(admin);
    setForm({
      email: admin.email,
      password: '',           // 비밀번호는 빈칸 = 변경 안 함
      name: admin.name,
      role: admin.role,
      storeId: admin.storeId ?? '',
      memo: admin.memo ?? '',
    });
    setFormError('');
  };

  const handleClose = () => { setMode(null); setEditTarget(null); };

  // ── 저장 (생성 or 수정) ──────────────────────────────
  const handleSave = async () => {
    if (!form.name) { setFormError('이름은 필수입니다.'); return; }
    if (mode === 'create' && (!form.email || !form.password)) {
      setFormError('이메일과 비밀번호는 필수입니다.'); return;
    }
    if (form.role === 'manager' && !form.storeId) {
      setFormError('매니저는 지점을 선택해야 합니다.'); return;
    }

    setSaving(true);
    try {
      const payload = {
        name: form.name,
        role: form.role,
        storeId: form.role === 'master' ? null : form.storeId,
        memo: form.memo,
        ...(form.password ? { password: form.password } : {}),
      };

      if (mode === 'create') {
        await api.post('/admins', { ...payload, email: form.email, password: form.password });
      } else {
        await api.put(`/admins/${editTarget._id}`, payload);
      }

      handleClose();
      fetchData();
    } catch (err) {
      setFormError(err.response?.data?.message || '저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handleOpenPwReset = (admin) => {
    setPwTarget(admin);
    setPwForm({ password: '', confirm: '' });
    setPwError('');
  };

  const handlePwSave = async () => {
    if (!pwForm.password) { setPwError('새 비밀번호를 입력해주세요.'); return; }
    if (pwForm.password !== pwForm.confirm) { setPwError('비밀번호가 일치하지 않습니다.'); return; }
    setPwSaving(true);
    try {
      await api.put(`/admins/${pwTarget._id}`, { password: pwForm.password });
      setPwTarget(null);
    } catch (err) {
      setPwError(err.response?.data?.message || '비밀번호 변경에 실패했습니다.');
    } finally {
      setPwSaving(false);
    }
  };

  const handleDeactivate = async (admin) => {
    if (!window.confirm(`'${admin.name}' 계정을 비활성화하시겠습니까?`)) return;
    try {
      await api.patch(`/admins/${admin._id}/deactivate`);
      fetchData();
    } catch {
      alert('비활성화에 실패했습니다.');
    }
  };

  const getStoreName = (storeId) =>
    stores.find((s) => s._id === storeId)?.name ?? '-';

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700} sx={{ whiteSpace: "nowrap" }}>계정 관리</Typography>
          <Typography variant="body2" color="text.secondary">
            관리자 계정 {admins.length}개
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<PersonAddIcon />} onClick={handleOpenCreate}>
          신규 계정 추가
        </Button>
      </Box>

      <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
        {/* 모바일은 핵심만 (이름/역할/상태/관리). 이메일/지점/메모/생성일은 md+에서 */}
        <Table sx={{ "& td[align=\"center\"], & th[align=\"center\"]": { whiteSpace: "nowrap" } }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#fafafa' }}>
              <TableCell sx={{ fontWeight: 700 }}>이름</TableCell>
              <TableCell sx={{ fontWeight: 700, display: { xs: 'none', md: 'table-cell' } }}>이메일</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700 }}>역할</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700, display: { xs: 'none', lg: 'table-cell' } }}>지점</TableCell>
              <TableCell sx={{ fontWeight: 700, display: { xs: 'none', lg: 'table-cell' } }}>메모</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700 }}>상태</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700, display: { xs: 'none', md: 'table-cell' } }}>생성일</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700 }}>관리</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>로딩 중...</TableCell>
              </TableRow>
            ) : admins.map((admin) => (
              <TableRow key={admin._id} hover>
                <TableCell sx={{ fontWeight: 600 }}>{admin.name}</TableCell>
                <TableCell sx={{ color: '#555', display: { xs: 'none', md: 'table-cell' }, wordBreak: 'break-all' }}>{admin.email}</TableCell>
                <TableCell align="center">
                  <Chip
                    label={roleMap[admin.role]?.label ?? admin.role}
                    size="small"
                    sx={{
                      ...tableTagSx(84),
                      backgroundColor: roleMap[admin.role]?.color,
                      color: '#fff',
                    }}
                  />
                </TableCell>
                <TableCell align="center" sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                  {admin.role === 'master'
                    ? <Typography variant="caption" color="text.secondary">전체 지점</Typography>
                    : getStoreName(admin.storeId)
                  }
                </TableCell>
                <TableCell sx={{ color: '#888', fontSize: 13, maxWidth: 200, display: { xs: 'none', lg: 'table-cell' } }}>
                  {admin.memo || <span style={{ color: '#ccc' }}>-</span>}
                </TableCell>
                <TableCell align="center">
                  <Box sx={statusChipSx(admin.isActive ? 'success' : 'neutral')}>
                    {admin.isActive ? '활성' : '비활성'}
                  </Box>
                </TableCell>
                <TableCell align="center" sx={{ color: '#888', fontSize: 13, display: { xs: 'none', md: 'table-cell' } }}>
                  {new Date(admin.createdAt).toLocaleDateString('ko-KR')}
                </TableCell>
                <TableCell align="center">
                  <Stack direction="row" spacing={0.75} justifyContent="center">
                    <Button variant="contained" onClick={() => handleOpenEdit(admin)} sx={actionBtnSx}>
                      수정
                    </Button>
                    <Button variant="contained" color="warning" onClick={() => handleOpenPwReset(admin)} sx={actionBtnSx}>
                      재설정
                    </Button>
                    {admin.isActive && (
                      <Button variant="contained" color="error" onClick={() => handleDeactivate(admin)} sx={actionBtnSx}>
                        비활성화
                      </Button>
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* 비밀번호 재설정 다이얼로그 */}
      <Dialog open={!!pwTarget} onClose={() => setPwTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle fontWeight={700}>비밀번호 변경</DialogTitle>
        <DialogContent>
          {pwError && <Alert severity="error" sx={{ mb: 2 }}>{pwError}</Alert>}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 1 }}>
            <strong>{pwTarget?.name}</strong> ({pwTarget?.email}) 계정의 비밀번호를 변경합니다.
          </Typography>
          <TextField
            label="새 비밀번호" type="password" fullWidth size="small" sx={{ mb: 2 }}
            value={pwForm.password}
            onChange={(e) => setPwForm({ ...pwForm, password: e.target.value })}
            autoFocus
          />
          <TextField
            label="새 비밀번호 확인" type="password" fullWidth size="small"
            value={pwForm.confirm}
            onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })}
            error={!!pwForm.confirm && pwForm.password !== pwForm.confirm}
            helperText={pwForm.confirm && pwForm.password !== pwForm.confirm ? '비밀번호가 일치하지 않습니다.' : ''}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setPwTarget(null)}>취소</Button>
          <Button variant="contained" color="warning" onClick={handlePwSave} disabled={pwSaving}>
            {pwSaving ? '변경 중...' : '변경'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 생성/수정 다이얼로그 */}
      <Dialog open={!!mode} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle fontWeight={700}>
          {mode === 'create' ? '신규 관리자 계정 추가' : '계정 수정'}
        </DialogTitle>
        <DialogContent>
          {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}

          <TextField
            label="이름" fullWidth size="small" sx={{ mb: 2, mt: 1 }}
            value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
          />

          {mode === 'create' && (
            <TextField
              label="이메일" type="email" fullWidth size="small" sx={{ mb: 2 }}
              value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
          )}

          {mode === 'edit' && (
            <TextField
              disabled fullWidth size="small" sx={{ mb: 2 }}
              value={form.email} label="이메일 (변경 불가)"
            />
          )}

          {mode === 'create' && (
            <TextField
              label="비밀번호"
              type="password" fullWidth size="small" sx={{ mb: 2 }}
              value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
            />
          )}

          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>역할</InputLabel>
            <Select value={form.role} label="역할" onChange={(e) => setForm({ ...form, role: e.target.value, storeId: '' })}>
              <MenuItem value="master">MASTER (전체 지점)</MenuItem>
              <MenuItem value="manager">MANAGER (지점 담당)</MenuItem>
            </Select>
          </FormControl>

          {form.role === 'manager' && (
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel>담당 지점</InputLabel>
              <Select value={form.storeId} label="담당 지점" onChange={(e) => setForm({ ...form, storeId: e.target.value })}>
                {stores.map((s) => (
                  <MenuItem key={s._id} value={s._id}>{s.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <TextField
            label="메모" fullWidth size="small" multiline rows={2}
            placeholder="예: 전주점 담당자, 2호점 오픈 시 전환 예정..."
            value={form.memo} onChange={(e) => setForm({ ...form, memo: e.target.value })}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose}>취소</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? '저장 중...' : '저장'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
